/**
 * Shared launched-edit dispatch — one implementation for CLW and the
 * LaunchCampaignDetails page so the two surfaces can't drift.
 *
 * Launched campaigns can't take the pre-launch whole-document PUT, so the
 * pending grid edits (the structured requests built by
 * buildLaunchedEditRequests.js) are folded into ONE batch edit-campaign
 * request: POST /api/v2/campaigns/:id/edit with an `edits` array. The
 * backend keeps the single-edit path unchanged and, for a batch, transports
 * the structured edits through the chat metadata `config_payload` (which
 * bypasses the LLM message analyzer), so the Python campaign editor applies
 * the exact field keys / values the grid staged.
 *
 * Return shape:
 *   { ok: true, messageId }            — dispatch accepted
 *   { ok: false, error }               — dispatch refused (nothing applied)
 */

const ENTITY_TYPE_BY_NOUN = {
  'line item': 'line_item',
  'ad group': 'ad_group',
  'asset group': 'asset_group',
  'ad set': 'ad_set',
  campaign: 'campaign',
};

/**
 * Convert buildLaunchedEditRequests output into the `/edit` batch payload.
 * `platformCampaignId` may be a comma-joined list for multi-campaign Google
 * Ads plans; each edit's rowContext.campaignIdx selects the matching id.
 * Pure — exported for tests.
 */
export function toBatchEdits(requests, platformCampaignId = '') {
  const campaignIds = String(platformCampaignId || '').split(',').map((s) => s.trim()).filter(Boolean);
  let editSeq = 0;
  return (requests || []).map((req) => (req.ops || []).map((op) => {
    editSeq += 1;
    const rowCampaignIdx = req.rowContext?.campaignIdx;
    // Fall back to the positional row id (`gads-<campaignIdx>-<unitIdx>`) when
    // the grid row was not found and no context was published.
    const rowIdCampaignIdx = /^gads-(\d+)-\d+$/.exec(String(req.rowId || ''))?.[1];
    const campaignIdx = Number.isInteger(rowCampaignIdx)
      ? rowCampaignIdx
      : (rowIdCampaignIdx != null ? Number(rowIdCampaignIdx) : null);
    const perEditCampaignId = Number.isInteger(campaignIdx) && campaignIds[campaignIdx]
      ? campaignIds[campaignIdx]
      : null;
    return {
      editId: `${req.rowId || `row-${editSeq}`}:${editSeq}`,
      rowId: req.rowId,
      entityType: ENTITY_TYPE_BY_NOUN[req.entityNoun] || 'campaign',
      ...(perEditCampaignId ? { platformCampaignId: perEditCampaignId } : {}),
      targetEntities: req.targetEntities || [],
      operation: op.op === 'set' ? 'set_field' : `list_${op.op}`,
      field: op.field,
      value: op.value,
      ...(op.newValue !== undefined ? { newValue: op.newValue } : {}),
      // Pre-change value, recorded so the backend can build the undo payload.
      ...(op.oldValue !== undefined ? { oldValue: op.oldValue } : {}),
      fieldLabel: op.label,
      description: req.description,
      rowContext: req.rowContext || null,
    };
  })).flat();
}

/**
 * Legacy per-row single-edit dispatch. GroundTruth and Facebook are
 * deliberately kept on the original one-message-per-row path (GT must not
 * change; FB row ids are positional and the legacy prose path is what those
 * surfaces have always used). Callers branch to this based on the request
 * family.
 */
export async function dispatchLegacySingleEdits({
  api, getApiUrl, campaignId, accountId, userId, platformCampaignId, requests,
}) {
  let ok = 0;
  let firstError = null;
  for (const req of requests) {
    try {
      const res = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/edit`), {
        accountId,
        userId,
        platformCampaignId,
        description: req.description,
        targetEntities: req.targetEntities,
      });
      if (res.data?.success) ok += 1;
      else if (!firstError) firstError = res.data?.error || 'Edit dispatch failed';
    } catch (e) {
      if (!firstError) firstError = e?.response?.data?.error || e.message || 'Edit dispatch failed';
    }
  }
  return { ok: ok > 0, firstError, count: ok };
}

/**
 * Dispatch one batch edit-campaign request.
 *
 * @param {object} args
 * @param {object} args.api          axios-like api instance
 * @param {function} args.getApiUrl  URL prefix helper
 * @param {string} args.campaignId   internal campaign id (route param)
 * @param {string} args.accountId
 * @param {string} args.userId
 * @param {string} args.platformCampaignId  live platform campaign id(s)
 * @param {Array}  args.requests     buildLaunchedEditRequests output
 * @param {string} [args.destination] campaign destination, for platform
 *                                    routing the requests don't encode
 */
export async function dispatchLaunchedEdits({
  api, getApiUrl, campaignId, accountId, userId, platformCampaignId, requests, destination = '',
}) {
  // GT / FB keep the pre-batch, one-message-per-row behavior exactly.
  // YouTube DV360 has a single-edit executor but no batch executor, so it
  // takes the same legacy path instead of being rejected by the batch gate.
  const youtubeDv360 = String(destination || '').toLowerCase() === 'youtube-dv360';
  if (youtubeDv360 || (requests || []).some((r) => r.family === 'gt' || r.family === 'fb')) {
    return dispatchLegacySingleEdits({
      api, getApiUrl, campaignId, accountId, userId, platformCampaignId, requests,
    });
  }

  const edits = toBatchEdits(requests, platformCampaignId);
  if (!edits.length) return { ok: false, error: 'No structured edits to dispatch' };

  try {
    const res = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/edit`), {
      accountId,
      userId,
      platformCampaignId,
      description: `Apply ${edits.length} CLW field edit${edits.length === 1 ? '' : 's'}`,
      edits,
    });
    if (res.data?.success) {
      return { ok: true, messageId: res.data.messageId || null, count: edits.length };
    }
    return { ok: false, error: res.data?.error || 'Edit dispatch failed' };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.error || e.message || 'Edit dispatch failed' };
  }
}

export default { toBatchEdits, dispatchLaunchedEdits };
